import { P2GAlert, P2GBox, P2GImage } from '@parcel2go/core-ui';
import { P2GGrid } from '@parcel2go/core-ui';
import { P2GLink } from '@parcel2go/core-ui';
import { P2GText } from '@parcel2go/core-ui';
import { ReactNode } from 'react';
import { Dimensions, Service } from '~/lib/api/smartsend/types';
import { FixedColumn } from '../../FixedColumn';
import { EditType } from './EditType';
import { useServiceLogo } from '../../../../../hooks/use-service-logo';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';

interface props {
  errors?: string[];
  service: Service;
  edit: (type: EditType) => void;
  book?: () => void;
  children?: ReactNode;
  revealText?: string;
  variant?: 'body2';
  dims?: Dimensions;
  senderIsoCode?: string;
  recipientIsoCode?: string;
  itemId: string;
  showAsAlert?: boolean;
}

interface propsInner extends props {
  editService: () => void;
}

interface ServiceImageProps {
  onClick?: () => void;
  service: { Provider: string; Id: string; Name: string };
  width?: number;
}

export function ServiceImage(props: ServiceImageProps) {
  const { getSrc } = useServiceLogo();

  const width = props.width || 70;
  const margin = 4;

  const style = {
    maxHeight: `${(width - 2 * margin) / 2}px`,
    maxWidth: `${width - 2 * margin}px`,
    margin: `${margin}px`
  };

  return (
    <div style={{ width: `${width}px` }} onClick={props.onClick}>
      <P2GImage style={style} src={getSrc(props.service)} alt={props.service?.Name} />
    </div>
  );
}

export function ItemServiceInner(props: propsInner) {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  if (props.showAsAlert) {
    return (
      <P2GAlert type="success" icon={<ServiceImage width={60} service={props.service} onClick={props.editService} />}>
        <P2GText variant={props.variant}>
          <strong>{props.service?.Name}</strong>
          {props.editService && (
            <P2GLink ml={1} variant="body2" onClick={props.editService}>
              {props.revealText || t('CHANGE_LINK', 'Change')}
            </P2GLink>
          )}
        </P2GText>
        <P2GBox mt={1}>{props.children}</P2GBox>
      </P2GAlert>
    );
  }

  if (SettingsCache.settings.Features.includes('ServiceLogos')) {
    return (
      <P2GBox>
        <FixedColumn
          left={<ServiceImage width={60} service={props.service} onClick={props.editService} />}
          right={
            <>
              <P2GText variant={props.variant}>
                <strong>{props.service?.Name}</strong>
                {props.editService && (
                  <P2GLink ml={1} variant="body2" onClick={props.editService}>
                    {props.revealText || t('CHANGE_LINK', 'Change')}
                  </P2GLink>
                )}
              </P2GText>
              <P2GBox mt={1}>{props.children}</P2GBox>
            </>
          }
          width={65}
        />
      </P2GBox>
    );
  }
  return (
    <P2GBox>
      <P2GBox onClick={props.editService}>
        <P2GText variant={props.variant}>
          <strong>{props.service?.Name}</strong>
          {props.editService && (
            <P2GLink ml={1} variant="body2" onClick={props.editService}>
              {props.revealText || t('CHANGE_LINK', 'Change')}
            </P2GLink>
          )}
        </P2GText>
      </P2GBox>
      <P2GBox mt={1}>{props.children}</P2GBox>
    </P2GBox>
  );
}

export default function ItemService(props: props) {
  const hasService = !!props.service?.Id;
  const editService = () => props.edit && props.edit('SERVICE');
  return (
    hasService && (
      <P2GBox p={props.showAsAlert ? 0 : 1}>
        <ItemServiceInner
          {...props}
          editService={editService}
          variant={props.variant}
          senderIsoCode={props.senderIsoCode}
          recipientIsoCode={props.recipientIsoCode}
        />
      </P2GBox>
    )
  );
}

export function ItemServiceMobile(props: props) {
  const hasService = !!props.service?.Id;
  const editService = () => props.edit && props.edit('SERVICE');
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  return (
    <P2GGrid container gap={1} alignItems="center" wrap="nowrap">
      <P2GGrid item flexGrow={1}>
        {hasService && (
          <ItemServiceInner
            {...props}
            editService={editService}
            variant={props.variant}
            senderIsoCode={props.senderIsoCode}
            recipientIsoCode={props.recipientIsoCode}
          />
        )}
      </P2GGrid>
      <P2GGrid item flexShrink={1}>
        <P2GLink variant={props.variant} onClick={editService}>
          {props.errors?.length ? t('FIX_LINK', 'Fix') : props.revealText || t('CHANGE_LINK', 'Change')}
        </P2GLink>
      </P2GGrid>
    </P2GGrid>
  );
}
